import cx from 'classnames';
import React from 'react';
import PropTypes from 'prop-types';
import { reduxForm, Field } from 'redux-form';
import Select from 'react-select';
import './Form.scss';

const TextField = ({ description, input, label, meta: { error } }) => (
  <div className={cx('explorer__form__group', { 'explorer__form__group--error': !!error })}>
    <label htmlFor={input.name}>{label}</label>
    {description ? <p>{description}</p> : null}
    <input type="text" className="explorer__form__input" id={input.name} name={input.name} {...input} />
    {(error && <span className="explorer__form__message">{error}</span>)}
  </div>
);
TextField.propTypes = {
  description: PropTypes.string,
  input: PropTypes.object.isRequired,
  label: PropTypes.string,
  meta: PropTypes.object,
};

const SelectField = ({ description, input, label = 'Untitled', name, options = [], multi = false, meta: { error } }) => {
  // react-select expects option objects for `value`/`onChange`.
  // Convert redux-form simple values to option objects and back.
  const findSelected = () => {
    if (multi) {
      if (!input.value) return [];
      return options.filter(o => (Array.isArray(input.value) ? input.value.indexOf(o.value) !== -1 : false));
    }
    return options.find(o => o.value === input.value) || null;
  };

  const handleChange = (val) => {
    if (multi) {
      input.onChange(val ? val.map(v => v.value) : []);
    } else {
      input.onChange(val ? val.value : null);
    }
  };

  const fieldId = `${input.name}-select`;
  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      border: `1px solid ${state.isFocused ? '#0056b3' : '#d0d0d0'}`,
      boxShadow: state.isFocused ? '0 0 0 3px rgba(0,86,179,0.12)' : 'none',
      boxSizing: 'border-box',
      borderRadius: 3,
    }),
    input: (provided) => ({ ...provided, padding: '0.25rem 0.5rem' }),
    menu: (provided) => ({ ...provided, border: '1px solid #d0d0d0', boxShadow: 'none' }),
  };

  return (
    <div className="explorer__form__group">
      <label htmlFor={fieldId}>{label}</label>
      {description ? <p>{description}</p> : null}
      <div>
        <Select
          name={input.name}
          id={fieldId}
          inputId={fieldId}
          styles={customStyles}
          options={options}
          value={findSelected()}
          isMulti={multi}
          isSearchable={true}
          placeholder="Select..."
          onBlur={() => input.onBlur(input.value)}
          onChange={handleChange}
        />
      </div>
      {(error && <span className="explorer__form__error">{error}</span>)}
    </div>
  );
};
SelectField.propTypes = {
  description: PropTypes.string,
  input: PropTypes.object.isRequired,
  label: PropTypes.string,
  options: PropTypes.array.isRequired,
  multi: PropTypes.bool,
};

const RadioGroup = ({input, meta, options}) => {
  const hasError = meta.touched && meta.error;
  return(
    <div>
      {options.map(o => (
        <p key={o.value}>
          <input id={o.value} type="radio" {...input} value={o.value} checked={o.value === input.value} />
          <label htmlFor={o.value}>{o.title}</label>
        </p>
      ))}
      {hasError && <span className="explorer__form__error">{meta.error}</span>}
    </div>
  );
}

class Form extends React.Component {
  constructor(props){
    super(props);
    this.clearForm = this.clearForm.bind(this);
  }

  clearForm(e) {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    if (this.props && typeof this.props.reset === 'function') {
      this.props.reset();
    } else {
      this.props.change && this.props.change('tradeFlow', null);
      this.props.change && this.props.change('countries', null);
      this.props.change && this.props.change('hsCode', null);
    }
  }

  render() {
    const { error, handleSubmit, formOptions } = this.props;
    return (
      <form className="explorer__form" onSubmit={handleSubmit}>
        <h1 className="Header-1"><b>Search FTA Rates</b></h1>
        <br />
          <fieldset className="explorer__form__group">
            <legend>1.  Are you Importing or Exporting?</legend>
              <div className="explorer__form__radio">
                 <Field component={RadioGroup} name="tradeFlow" options={[
                   { title: <span>You are <u>exporting</u> goods from the United States to an FTA partner.</span>, value: 'Exporting'},
                   { title: <span>You are <u>importing</u> goods from an FTA Partner into the United States.</span>, value: 'Importing'}
                 ]} />
              </div>
          </fieldset>

          <Field
            component={SelectField} name="countries" label="2. Please select an FTA Partner:" options={formOptions.countries}
            description=""
          />
          <Field
            component={TextField} name="hsCode" label="3. Search by an HS Code:"
            description="If you know your HS code, enter it here (enter 4-10 numeric codes to identify a product).  This field is not required."
          />
          <p>
          If you do not know your HS code, the <a href="https://uscensus.prod.3ceonline.com/" target="_blank">Schedule B Search Engine</a> can help you determine your HS code by allowing you to search by keyword or product description.   If you are still having trouble finding your products, please see <a href="https://www.trade.gov/faq/fta-tariff-tool-faq" target="_blank">FAQ page</a> for more information.
          </p>
          <div className="explorer__form__group explorer__form__actions">
            <button className="explorer__form__submit pure-button pure-button-primary" type="submit" onClick={handleSubmit} disabled={!!error}>
              <i className="fa fa-paper-plane" /> Search
            </button>
            <button className="explorer__form__clear pure-button" type="reset" onClick={this.clearForm} >
              Clear
            </button>
          </div>
      </form>
    );
  }
}
Form.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  reset: PropTypes.func,
  change: PropTypes.func,
};

export default reduxForm({
  form: 'form',
})(Form);
